import { Link } from "react-router-dom";
import {
    HeartPulse, Banknote, ShoppingBag, Truck, Factory, Building2, Check, ArrowUpRight,
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";
import { INDUSTRIES } from "../data/industries";

const ICONS = {
    healthcare: <HeartPulse className="w-6 h-6" />,
    bfsi: <Banknote className="w-6 h-6" />,
    retail: <ShoppingBag className="w-6 h-6" />,
    logistics: <Truck className="w-6 h-6" />,
    manufacturing: <Factory className="w-6 h-6" />,
    tech: <Building2 className="w-6 h-6" />,
};

const MATRIX_ROWS = [
    {
        industry: "Healthcare",
        aiUseCases: "Prior-auth, clinical NLP, deteriorating risk",
        talentNeeds: "FHIR specialists, NLP scientists, HIPAA-aware engineers",
        engineeringNeeds: "EHR integrations, secure data pipelines",
        commonWorkflows: "Document intake, patient outreach, risk dashboards",
    },
    {
        industry: "BFSI",
        aiUseCases: "KYC review, fraud detection, credit underwriting",
        talentNeeds: "Risk/quant engineers, model risk specialists",
        engineeringNeeds: "Core banking APIs, high-throughput pipelines",
        commonWorkflows: "Sanctions screening, claims review, compliance audits",
    },
    {
        industry: "Retail",
        aiUseCases: "Semantic search, conversational recommendations",
        talentNeeds: "Search/recsys engineers, conversational designers",
        engineeringNeeds: "Catalog processing, dynamic pricing databases",
        commonWorkflows: "Product discovery, catalog moderation, support copilots",
    },
    {
        industry: "Logistics",
        aiUseCases: "Route optimisation, exception routing, POD parsing",
        talentNeeds: "Optimisation specialists, geospatial developers",
        engineeringNeeds: "EDI integrations, TMS/WMS custom extensions",
        commonWorkflows: "BOL intake, customs exceptions, ETA tracking",
    },
    {
        industry: "Manufacturing",
        aiUseCases: "Predictive maintenance, defect computer-vision",
        talentNeeds: "IIoT engineers, plant reliability analysts",
        engineeringNeeds: "OT-to-cloud pipelines, industrial gateways",
        commonWorkflows: "Telemetry alerting, SOP assistants, catalog RFx",
    },
    {
        industry: "SaaS",
        aiUseCases: "In-app copilots, LLM eval, support orchestration",
        talentNeeds: "Senior full-stack developers, MLOps specialists",
        engineeringNeeds: "Feature flags, evaluations harnesses, multi-tenant APIs",
        commonWorkflows: "Onboarding assistants, prompt tracing, pipeline monitoring",
    },
];

export default function Industries() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="Industries"
                description="Industry-specific AI, product engineering and talent solutions across healthcare, BFSI, retail, logistics, manufacturing and technology."
            />

            <PageHero
                eyebrow="Industries"
                title="Domain depth, AI engineering,"
                titleAccent="the right talent — in one partner."
                description="Every solution we deliver respects your regulatory, operational and customer realities — because the people building it are specialists in your industry."
                primaryCta={{ to: "/contact", label: "Discuss your industry use case" }}
                secondaryCta={{ to: "/ai-solutions", label: "Explore services" }}
                bullets={["Healthcare", "BFSI", "Retail", "Logistics", "Manufacturing", "Tech & SaaS"]}
            />

            {/* Matrix Table */}
            <section className="bg-white py-12 md:py-16 border-b border-[#303030]/8">
                <div className="pqx-container">
                    <div className="max-w-3xl mb-8">
                        <span className="pqx-eyebrow">Industry matrix</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            Capabilities mapped by domain.
                        </h2>
                    </div>

                    <div className="overflow-x-auto rounded-2xl border border-[#303030]/10 shadow-sm bg-[#FAFAF8]">
                        <table className="w-full border-collapse text-left text-sm text-[#303030]/80">
                            <thead className="bg-[#303030] text-white text-[11px] uppercase tracking-[0.16em]">
                                <tr>
                                    <th className="p-4 font-semibold">Industry</th>
                                    <th className="p-4 font-semibold">AI Use Cases</th>
                                    <th className="p-4 font-semibold">Talent Needs</th>
                                    <th className="p-4 font-semibold">Engineering Needs</th>
                                    <th className="p-4 font-semibold">Common Workflows</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#303030]/8 font-medium">
                                {MATRIX_ROWS.map((row) => (
                                    <tr key={row.industry} className="hover:bg-white transition-colors duration-150">
                                        <td className="p-4 font-bold text-[#303030]">{row.industry}</td>
                                        <td className="p-4 text-[13px]">{row.aiUseCases}</td>
                                        <td className="p-4 text-[13px]">{row.talentNeeds}</td>
                                        <td className="p-4 text-[13px]">{row.engineeringNeeds}</td>
                                        <td className="p-4 text-[13px]">{row.commonWorkflows}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Cards List */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="industries-list">
                <div className="pqx-container space-y-6">
                    {INDUSTRIES.map((ind, i) => (
                        <Link
                            key={ind.slug}
                            to={`/industries/${ind.slug}`}
                            data-testid={`industry-${ind.slug}`}
                            className="block rounded-3xl bg-white border border-[#303030]/8 overflow-hidden hover:border-[#FF4040]/30 hover:shadow-[0_20px_50px_-30px_rgba(255,64,64,0.25)] transition-all duration-300"
                        >
                            <div className="grid lg:grid-cols-[1fr_2fr]">
                                {/* Left Side Branding */}
                                <div className="bg-[#191919] text-[#FAFAF8] p-8 md:p-10 relative overflow-hidden flex flex-col justify-between h-full min-h-[260px]">
                                    <div className="absolute inset-0 pqx-grid-bg-dark opacity-30 pointer-events-none" />
                                    <div className="relative">
                                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55">
                                            Industry 0{i + 1}
                                        </span>
                                        <div className="mt-5 w-14 h-14 grid place-items-center rounded-2xl bg-[#FF4040]/15 text-[#FF4040]">
                                            {ICONS[ind.slug]}
                                        </div>
                                        <h2 className="pqx-display text-2xl md:text-3xl mt-6">
                                            {ind.name}
                                        </h2>
                                    </div>

                                    <div className="relative pt-6 border-t border-white/10 flex items-center justify-between text-xs text-[#FF4040] font-semibold">
                                        <span>Explore industry details</span>
                                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </div>
                                </div>

                                {/* Right Side 4-part Structure */}
                                <div className="p-8 md:p-10 grid sm:grid-cols-2 gap-6 md:gap-8">
                                    {/* 1. Industry Pain */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                            01 · Industry Pain
                                        </p>
                                        <p className="mt-2 text-[13.5px] text-[#303030]/75 leading-relaxed">
                                            {ind.challenges[0]}
                                        </p>
                                    </div>

                                    {/* 2. AI Use Case */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                            02 · AI Use Case
                                        </p>
                                        <p className="mt-2 text-[13.5px] text-[#303030]/75 leading-relaxed font-semibold">
                                            {ind.useCases[0].title}
                                        </p>
                                        <p className="mt-1 text-[12.5px] text-[#303030]/60 leading-relaxed">
                                            {ind.useCases[0].copy}
                                        </p>
                                    </div>

                                    {/* 3. Talent we provide */}
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                            03 · Talent We Provide
                                        </p>
                                        <p className="mt-2 text-[13.5px] text-[#303030]/75 leading-relaxed">
                                            {ind.talent.split(",").slice(0, 2).join(",")}...
                                        </p>
                                    </div>

                                    {/* 4. Example Outcome */}
                                    <div className="p-4 rounded-xl bg-[#FAFAF8] border border-[#303030]/5 flex flex-col justify-between">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#303030]/45 font-semibold">
                                                04 · Example Outcome
                                            </p>
                                            <p className="mt-1 text-[12px] font-bold text-[#303030]/75 line-clamp-2">
                                                {ind.caseStudy.title}
                                            </p>
                                        </div>
                                        <div className="mt-3 flex gap-4 border-t border-[#303030]/8 pt-2.5">
                                            {ind.caseStudy.metrics.slice(0, 2).map((m) => (
                                                <div key={m.label}>
                                                    <span className="text-[15px] font-bold text-[#FF4040] block">{m.value}</span>
                                                    <span className="text-[9px] uppercase tracking-wider text-[#303030]/55">{m.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <FinalCTA
                title="Bring your industry context."
                description="Our teams co-create roadmaps that respect your regulatory, operational and customer realities — and deliver measurable wins."
                primary={{ to: "/contact", label: "Discuss your industry use case" }}
                secondary={{ to: "/products", label: "See product accelerators" }}
            />
        </div>
    );
}
